import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import { formatStatsValue } from 'utils/Formatters';
import Switch from 'react-switch';
import { Bar } from 'react-chartjs-2';
import _ from 'lodash';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FlexLoadingSpinner } from 'components/helpers/LoadingSpinner';
import {
  Chart as ChartJS,
} from 'chart.js';

ChartJS.register(
  ChartDataLabels
);

const ChartData = ({ dataset }) => {
  return (
    <>
      {
        <div>
          <Bar
            data={{
              labels: [
                'Communication',
                'Fine motor',
                'Gross motor',
                'Personal-Social',
                'Problem solving',
              ],
              datasets: [
                { label: 'Monitor', data: dataset[0], backgroundColor: 'rgba(245,166,35,0.4)' },
                { label: 'Refer', data: dataset[1], backgroundColor: 'rgba(201,24,211,0.4)' },
              ],
            }}
            options={{
              indexAxis: 'y',
              scales: {
                x: {
                  stacked: true,
                },
                y: {
                  stacked: true,
                },
              },
              plugins: {
                datalabels: {
                  display: true,
                  font: { size: 16, weight: 'bold' },
                  formatter: (value, _context) => value === 0 ? '' : `${Math.round(value)}`,
                },
              },
              tooltips: { enabled: false },
            }}
          />
        </div>
      }
    </>
  );
};

ChartData.propTypes = {
  dataset: PropTypes.array.isRequired,
}

const ASQStatHorizontal = ({ row, keyStr, withBr }) => {
  return (
    <div key={keyStr}>
      <span className='u-txt--stat-value u-vertical-stats'>{formatStatsValue(row.value)}</span>
      {withBr ? <br /> : ''}
      <span className='u-txt--stat-label u-vertical-stats u-vertical-stats-label'>{row.label}</span>
    </div>
  )
};

ASQStatHorizontal.propTypes = {
  row: PropTypes.object.isRequired,
  keyStr: PropTypes.string,
  withBr: PropTypes.bool,
  horizontal: PropTypes.bool,
};

const ASQStat = ({ row, keyStr, withBr }) => {
  return (
    <div key={keyStr}>
      <span className='u-txt--stat-value'>{formatStatsValue(row.value)}</span>
      {withBr ? <br /> : ''}
      <span className='u-txt--stat-label'>{row.label}</span>
    </div>
  )
};

ASQStat.propTypes = {
  row: PropTypes.object.isRequired,
  keyStr: PropTypes.string,
  withBr: PropTypes.bool,
  horizontal: PropTypes.bool,
};

const ASQStatsCard = ({ asq3OnScheduleData, asq3MonitorData, asq3MonitorSections, asq3ReferData, asq3ReferSections, asq2Data, title, isLoading }) => {
  const prefix = kebabCase(title);
  const [isChart, setChart] = useState(false);

  const asq3ScheduleRow = (
    <tr key={`${prefix + 'asq-3-schedule'}`}>
      <td>
        <ASQStat row={asq3OnScheduleData} withBr={true} />
      </td>
      <td>

      </td>
    </tr>
  );

  const asq3MonitorSectionsRow = (
    <td>
      {asq3MonitorSections.map((row, idx) => (
        <ASQStatHorizontal row={row} withBr={false} key={`${prefix}-${idx} asq-3-monitor'`} />
      ))}
    </td>
  );

  const asq3MonitorRow = (
    <tr key={`${prefix} asq-3-monitor`}>
      <td>
        <ASQStat row={asq3MonitorData} withBr={true} />
      </td>
      {asq3MonitorSectionsRow}
    </tr>
  );

  const asq3ReferSectionsRow = (
    <td>
      {asq3ReferSections.map((row, idx) => (
        <ASQStatHorizontal row={row} withBr={false} key={`${prefix}-${idx} asq-3-refer'`} />
      ))}
    </td>
  );

  const asq3ReferRow = (
    <tr key={`${prefix}`}>
      <td>
        <ASQStat row={asq3ReferData} withBr={true} />
      </td>
      {asq3ReferSectionsRow}
    </tr>
  );

  const asq2Rows = asq2Data.map((row, idx) => (
    <tr key={`${prefix}-${idx} asq-2`}>
      <td>
        <ASQStat row={row} withBr={true} />
      </td>
      <td>

      </td>
    </tr>
  ));

  const table = (
    <table className='o-table o-table--bordered'>
      <tbody>
        {asq3ScheduleRow}
        {asq3MonitorRow}
        {asq3ReferRow}
        {asq2Rows}
      </tbody>
    </table>
  );

  const categories = [
    'communication',
    'fine motor',
    'gross motor',
    'personal-social',
    'problem solving',
  ];

  const monitors = categories.map((category) => {
    return _.find(asq3MonitorSections, (o) => o.label.match(new RegExp(category)))?.value
  });

  const refers = categories.map((category) => {
    return _.find(asq3ReferSections, (o) => o.label.match(new RegExp(category)))?.value
  });

  const chart = (
    <div style={{ padding: 40 }}>
      <ChartData dataset={[monitors, refers]} />
    </div>
  );

  return (
    <div className='column medium-12 small-12 o-dashboard-card__wrap'>
      <div>
        <strong>{title}</strong>
        <div className="o-asq-stats-card-toggle">
          <span>Table View</span>
          <Switch
            onColor="#5ac9ac"
            checkedIcon={false}
            uncheckedIcon={false}
            checked={isChart}
            onChange={(checked) => setChart(checked)}
            disabled={isLoading}
          />
          <span>Chart View</span>
        </div>
      </div>
      <div className='o-dashboard-card u-flex u-pading-bottom--gutter cs-bg--light'>
        <div style={{ width: 100 + '%' }} className='table-scroll'>
          {isLoading ? (<FlexLoadingSpinner />) : isChart ? chart : table}
        </div>
      </div>
    </div>
  )
}

ASQStatsCard.propTypes = {
  asq3OnScheduleData: PropTypes.object.isRequired,
  asq3MonitorData: PropTypes.object.isRequired,
  asq3MonitorSections: PropTypes.arrayOf(PropTypes.object).isRequired,
  asq3ReferData: PropTypes.object.isRequired,
  asq3ReferSections: PropTypes.arrayOf(PropTypes.object).isRequired,
  asq2Data: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
}


export default ASQStatsCard;
